<template>
  <div class="subscriptions-v3">
    <v-alert v-if="loadError" type="error">{{ loadError }}</v-alert>
    <v-alert v-if="paymentError" type="error">
      {{ paymentError }}
      <v-btn text small @click="loadPaymentTypes">Retry</v-btn>
    </v-alert>
    <v-alert v-if="message" type="success" dismissible @input="message = ''">
      {{ message }}
    </v-alert>

    <v-card class="mb-4">
      <v-card-title class="br">Search subscriptions</v-card-title>
      <v-card-text>
        <v-form ref="searchForm" @submit.prevent="search">
          <v-row>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filters.license_sn"
                label="License s/n"
                maxlength="33"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filters.user_id"
                label="User ID"
                type="number"
                min="1"
                step="1"
                :rules="optionalIdRules"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="filters.user_email"
                label="User email"
                maxlength="254"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="filters.user_name"
                label="User name"
                maxlength="100"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <date-input
                v-model="filters.date"
                label="Subscription covers date"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-select
                v-model="filters.active"
                :items="activeOptions"
                label="Active today"
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-btn
                color="primary"
                type="submit"
                :loading="loading"
                class="mr-2"
                >Search</v-btn
              >
              <v-btn text :disabled="loading" @click="resetSearch">Reset</v-btn>
            </v-col>
          </v-row>
        </v-form>
        <div>
          Active means today is within the subscription's start and end dates,
          inclusive (UTC). The date filter searches for periods covering the
          selected day.
        </div>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="subscriptions"
      :loading="loading"
      :options.sync="options"
      :server-items-length="total"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      class="elevation-1"
      :no-data-text="
        hasSearched
          ? 'No subscriptions found.'
          : 'Click Search to load subscriptions.'
      "
      @dblclick:row="onDoubleClick"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title><h2 class="br">Subscriptions</h2></v-toolbar-title>
          <v-spacer />
          <v-btn
            color="primary"
            small
            :disabled="saving || !paymentsLoaded"
            @click="createItem"
          >
            New subscription
          </v-btn>
          <v-btn
            icon
            :disabled="!hasSearched || loading || saving"
            aria-label="Refresh subscriptions"
            @click="initialize"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>
      </template>
      <template v-slot:item.user_name="{ item }">
        <div class="py-2">
          <div>{{ item.user_name || "—" }}</div>
          <div>{{ item.user_email }}</div>
          <div class="text-caption">User ID: {{ item.user_id }}</div>
        </div>
      </template>
      <template v-slot:item.license_sn="{ item }">
        <div>{{ item.license_sn }}</div>
      </template>
      <template v-slot:item.starts_at="{ item }">{{
        formatDate(item.starts_at)
      }}</template>
      <template v-slot:item.ends_at="{ item }">{{
        formatDate(item.ends_at)
      }}</template>
      <template v-slot:item.bandwidth="{ item }">{{
        formatBandwidth(item.bandwidth)
      }}</template>
      <template v-slot:item.br_release_cutoff="{ item }">
        {{
          item.br_release_cutoff
            ? formatDate(item.br_release_cutoff)
            : "All releases"
        }}
      </template>
      <template v-slot:item.active="{ item }">
        <v-chip
          small
          dark
          :color="item.active ? 'green darken-2' : 'red darken-2'"
        >
          {{ item.active ? "Yes" : "No" }}
        </v-chip>
      </template>
      <template v-slot:item.payment_type="{ item }">{{
        item.payment_type || "—"
      }}</template>
      <template v-slot:item.payment_reference="{ item }">{{
        item.payment_reference || "—"
      }}</template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          :disabled="saving || !paymentsLoaded"
          :aria-label="'Edit subscription ' + item.id"
          @click.stop="editItem(item)"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" :persistent="saving" max-width="650px">
      <v-card>
        <v-form ref="editForm" @submit.prevent="save">
          <v-card-title>{{
            editedItem.id ? "Subscription" : "New subscription"
          }}</v-card-title>
          <v-card-text>
            <v-alert v-if="saveError" type="error">{{ saveError }}</v-alert>
            <v-text-field
              v-model="editedItem.license_sn"
              label="License s/n"
              maxlength="33"
              :rules="licenseSnRules"
              :readonly="!!editedItem.id"
              :disabled="saving"
              hint="Serial number of an existing subscription license."
              persistent-hint
            />
            <v-row>
              <v-col cols="12" sm="6">
                <date-input
                  v-model="editedItem.starts_at"
                  label="Starts (UTC)"
                  required
                  :disabled="saving"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <date-input
                  v-model="editedItem.ends_at"
                  label="Ends (UTC, inclusive)"
                  :min="parseDate(editedItem.starts_at)"
                  required
                  :disabled="saving"
                />
              </v-col>
            </v-row>
            <v-text-field
              v-model="editedItem.bandwidth_gbit"
              label="Bandwidth (Gbit/s)"
              type="number"
              min="0.000001"
              step="0.000001"
              max="2147.483647"
              :rules="bandwidthRules"
              :disabled="saving"
            />
            <date-input
              v-model="editedItem.br_release_cutoff"
              label="BR release cutoff"
              clearable
              :disabled="saving"
              hint="Leave blank to allow all releases during the subscription period."
              persistent-hint
            />
            <v-text-field
              v-model="editedItem.monthly_price"
              label="Monthly price (EUR)"
              type="number"
              min="0"
              step="0.01"
              max="99999999.99"
              :rules="priceRules"
              :disabled="saving"
            />
            <v-select
              v-model="editedItem.payment_type_id"
              :items="paymentTypes"
              item-text="name"
              item-value="id"
              label="Payment type"
              :disabled="saving"
            />
            <v-text-field
              v-model="editedItem.payment_reference"
              label="Payment reference"
              maxlength="64"
              :counter="64"
              :disabled="saving"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="saving" @click="dialog = false"
              >Cancel</v-btn
            >
            <v-btn color="primary" text type="submit" :loading="saving"
              >Save</v-btn
            >
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DateInput from "../components/DateInput.vue";
import { admin_http_client } from "../services/api.js";
import { formatDate, parseDate } from "../utils/dates.js";

const emptyFilters = () => ({
  license_sn: "",
  user_id: "",
  user_email: "",
  user_name: "",
  date: "",
  active: null
});
const emptySubscription = () => ({
  id: null,
  license_sn: "",
  starts_at: "",
  ends_at: "",
  bandwidth_gbit: "",
  br_release_cutoff: "",
  monthly_price: "0.00",
  payment_type_id: null,
  payment_reference: ""
});
// Database IDs and bandwidth use PostgreSQL int4; one Gbit/s is 1,000,000 kbit/s.
const MAX_INT4 = 2147483647;
const KBIT_PER_GBIT = 1000000;
const validId = value =>
  /^[1-9][0-9]*$/.test(String(value)) && Number(value) <= MAX_INT4;
const bandwidthKbit = value => Math.round(Number(value) * KBIT_PER_GBIT);

export default {
  name: "subscriptions_v3",
  components: { DateInput },
  data: () => ({
    filters: emptyFilters(),
    appliedFilters: {},
    hasSearched: false,
    subscriptions: [],
    total: 0,
    options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: ["starts_at"],
      sortDesc: [true]
    },
    activeOptions: [
      { text: "All", value: null },
      { text: "Active", value: true },
      { text: "Inactive", value: false }
    ],
    headers: [
      { text: "User", value: "user_name" },
      { text: "License s/n", value: "license_sn" },
      { text: "Starts", value: "starts_at" },
      { text: "Ends", value: "ends_at" },
      { text: "Bandwidth", value: "bandwidth" },
      { text: "Release cutoff", value: "br_release_cutoff" },
      { text: "Active", value: "active" },
      { text: "Payment type", value: "payment_type" },
      { text: "Payment reference", value: "payment_reference" },
      { text: "Actions", value: "actions", sortable: false }
    ],
    paymentTypes: [],
    paymentsLoaded: false,
    paymentError: "",
    loading: false,
    saving: false,
    dialog: false,
    loadError: "",
    saveError: "",
    message: "",
    requestNumber: 0,
    today: "",
    editedItem: emptySubscription(),
    licenseSnRules: [
      value => (value && !!value.trim()) || "Enter a license serial number."
    ],
    optionalIdRules: [
      value => !value || validId(value) || "Enter a positive ID."
    ],
    bandwidthRules: [
      value =>
        (/^\d+(\.\d{1,6})?$/.test(String(value)) &&
          bandwidthKbit(value) > 0 &&
          bandwidthKbit(value) <= MAX_INT4) ||
        "Enter a positive bandwidth with at most 6 decimal places."
    ],
    priceRules: [
      value =>
        (/^\d+(\.\d{1,2})?$/.test(String(value)) &&
          Number(value) <= 99999999.99) ||
        "Enter a nonnegative price with at most 2 decimal places."
    ]
  }),
  watch: {
    options: {
      deep: true,
      handler() {
        this.initialize();
      }
    }
  },
  created() {
    this.loadPaymentTypes();
  },
  beforeDestroy() {
    this.requestNumber++;
  },
  methods: {
    async initialize() {
      // Table option changes must not load records before a search.
      if (!this.hasSearched) return;
      const request = ++this.requestNumber;
      this.loading = true;
      this.loadError = "";
      try {
        const response = await admin_http_client.get("/subscriptions_v3", {
          params: {
            ...this.appliedFilters,
            page: this.options.page,
            per_page: this.options.itemsPerPage,
            sort_by: this.options.sortBy[0] || "starts_at",
            descending: this.options.sortBy.length
              ? !!this.options.sortDesc[0]
              : true
          }
        });
        if (request !== this.requestNumber) return;
        this.subscriptions = response.data.items;
        this.total = response.data.total;
        this.today = response.data.today;
        // An edit can remove the final row on a filtered page.
        const lastPage = Math.max(
          1,
          Math.ceil(this.total / this.options.itemsPerPage)
        );
        if (this.options.page > lastPage) this.options.page = lastPage;
      } catch (error) {
        if (request !== this.requestNumber) return;
        this.subscriptions = [];
        this.total = 0;
        this.loadError = this.errorMessage(
          error,
          "Unable to load subscriptions. Please try again."
        );
      } finally {
        if (request === this.requestNumber) this.loading = false;
      }
    },
    async loadPaymentTypes() {
      this.paymentError = "";
      try {
        const response = await admin_http_client.get("/payment_types_v3");
        this.paymentTypes = [
          { id: null, name: "None (trial or complimentary)" },
          ...response.data
        ];
        this.paymentsLoaded = true;
      } catch (error) {
        this.paymentError = this.errorMessage(
          error,
          "Unable to load payment types."
        );
      }
    },
    search() {
      if (!this.$refs.searchForm.validate()) return;
      const params = {};
      Object.entries(this.filters).forEach(([key, value]) => {
        const normalized = typeof value === "string" ? value.trim() : value;
        if (
          normalized !== null &&
          normalized !== "" &&
          normalized !== undefined
        ) {
          params[key] = key === "date" ? parseDate(normalized) : normalized;
        }
      });
      this.appliedFilters = params;
      this.hasSearched = true;
      if (this.options.page !== 1) this.options.page = 1;
      else this.initialize();
    },
    resetSearch() {
      this.hasSearched = false;
      // Ignore any response from a search that was cleared.
      this.requestNumber++;
      this.filters = emptyFilters();
      this.appliedFilters = {};
      this.subscriptions = [];
      this.total = 0;
      this.loading = false;
      this.loadError = "";
      this.message = "";
      this.today = "";
      this.options.page = 1;
      this.$nextTick(() => this.$refs.searchForm.resetValidation());
    },
    formatDate,
    parseDate,
    formatBandwidth(value) {
      if (value >= KBIT_PER_GBIT) return value / KBIT_PER_GBIT + " Gbit/s";
      if (value >= 1000) return value / 1000 + " Mbit/s";
      return value + " kbit/s";
    },
    createItem() {
      if (this.saving || !this.paymentsLoaded) return;
      this.editedItem = emptySubscription();
      this.editedItem.license_sn = this.appliedFilters.license_sn || "";
      this.editedItem.starts_at = formatDate(
        this.today || new Date().toISOString().slice(0, 10)
      );
      this.editedItem.payment_type_id = this.paymentTypes.some(
        type => type.id === 1
      )
        ? 1
        : null;
      this.openDialog();
    },
    editItem(item) {
      if (this.saving || !this.paymentsLoaded) return;
      this.editedItem = {
        ...item,
        starts_at: formatDate(item.starts_at),
        ends_at: formatDate(item.ends_at),
        bandwidth_gbit: String(item.bandwidth / KBIT_PER_GBIT),
        monthly_price: Number(item.monthly_price).toFixed(2),
        br_release_cutoff: formatDate(item.br_release_cutoff),
        payment_reference: item.payment_reference || ""
      };
      this.openDialog();
    },
    openDialog() {
      this.saveError = "";
      this.message = "";
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.editForm) this.$refs.editForm.resetValidation();
      });
    },
    onDoubleClick(event, row) {
      this.editItem(row.item);
    },
    errorMessage(error, fallback) {
      const detail =
        error.response && error.response.data && error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) return detail.map(item => item.msg).join("; ");
      if (error.response && error.response.status === 403)
        return "Access denied. Check your administrator account and the nginx IP allowlist.";
      return fallback;
    },
    async save() {
      if (this.saving || !this.$refs.editForm.validate()) return;
      this.saving = true;
      this.saveError = "";
      const item = this.editedItem;
      const payload = {
        starts_at: parseDate(item.starts_at),
        ends_at: parseDate(item.ends_at),
        bandwidth: bandwidthKbit(item.bandwidth_gbit),
        br_release_cutoff: parseDate(item.br_release_cutoff),
        monthly_price: item.monthly_price,
        payment_type_id: item.payment_type_id,
        payment_reference: item.payment_reference.trim() || null
      };
      try {
        if (item.id) {
          await admin_http_client.put(`/subscriptions_v3/${item.id}`, payload);
        } else {
          payload.license_sn = item.license_sn.trim();
          await admin_http_client.post("/subscriptions_v3", payload);
        }
        this.dialog = false;
        this.message =
          "Subscription has been successfully " +
          (item.id ? "updated." : "created.");
        await this.initialize();
      } catch (error) {
        this.saveError = this.errorMessage(
          error,
          "Unable to save the subscription. Please try again."
        );
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
